import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Heart, ChevronRight, RefreshCw, Save } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GratitudePage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hasAccess) {
      fetchPrompts();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${API}/gratitude`);
      setPrompts(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta innehåll');
    } finally {
      setLoading(false);
    }
  };

  const getRandomPrompt = () => {
    const random = prompts[Math.floor(Math.random() * prompts.length)];
    setCurrentPrompt(random);
    setAnswer('');
  };

  const saveToJournal = async () => {
    if (!answer.trim()) return;
    setSaving(true);
    try {
      const content = `## Tacksamhet: ${currentPrompt.title}\n\n**${currentPrompt.prompt}**\n\n${answer}\n\n*${currentPrompt.follow_up}*`;
      await axios.post(`${API}/journal`, {
        content,
        tags: ['tacksamhet']
      }, { headers: getAuthHeader() });
      toast.success('Sparad i din dagbok');
      setCurrentPrompt(null);
      setAnswer('');
    } catch (error) {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Startsida
            </Button>
            <h1 className="font-heading text-xl font-light">Tacksamhet</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Tacksamhet" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="gratitude-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => currentPrompt ? setCurrentPrompt(null) : navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {currentPrompt ? 'Tillbaka' : 'Startsida'}
          </Button>
          <h1 className="font-heading text-xl font-light">Tacksamhet</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : currentPrompt ? (
          <div className="animate-fade-in space-y-8">
            <div className="bg-rose-100/50 rounded-2xl p-8 text-center">
              <Heart className="w-10 h-10 text-primary mx-auto mb-4" />
              <h2 className="font-heading text-2xl mb-2">{currentPrompt.title}</h2>
              <p className="text-xl text-foreground">{currentPrompt.prompt}</p>
            </div>

            <div className="bg-card rounded-2xl p-8 space-y-4">
              <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Skriv ditt svar här..."
                className="min-h-[200px] rounded-xl bg-secondary/30 border-0 focus-visible:ring-1 focus-visible:ring-primary resize-none"
              />
              <p className="text-sm text-muted-foreground italic">{currentPrompt.follow_up}</p>
            </div>

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={getRandomPrompt} className="rounded-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Annan fråga
              </Button>
              <Button onClick={saveToJournal} disabled={!answer.trim() || saving} className="rounded-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Spara i dagboken
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-10 space-y-3 animate-fade-in">
              <div className="w-16 h-16 mx-auto rounded-full bg-rose-100/50 flex items-center justify-center">
                <Heart className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-heading text-3xl font-light">Tacksamhet</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Daglig tacksamhetspraktik för att skifta fokus till det som är bra i ditt liv.
              </p>
            </div>

            <div className="flex justify-center mb-8">
              <Button onClick={getRandomPrompt} className="rounded-full px-8">
                <RefreshCw className="w-4 h-4 mr-2" />
                Slumpmässig fråga
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prompts.map((prompt, index) => (
                <button
                  key={prompt.id}
                  onClick={() => { setCurrentPrompt(prompt); setAnswer(''); }}
                  className="bg-rose-100/50 p-6 rounded-2xl text-left hover:shadow-soft transition-shadow duration-300 opacity-0 animate-slide-up group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg">{prompt.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{prompt.prompt}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
